import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, withCtx as _withCtx } from "vue"

import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
import { useRoute } from 'vue-router';

import { accountService } from '@exchange/libs/account/service/src';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';
import { marketService, MarketState } from '@exchange/libs/market/service/src';
import { launchdarkly } from '@exchange/libs/utils/launchdarkly/src';
import { SWAppUpdateChecker } from '@exchange/libs/utils/service-worker-update-checker/src';
import wsEmitter, { onMaxConnectAttempt, type WebSocketManagerName } from '@exchange/libs/utils/wss/src/lib/websocket-event-bus';

import ConnectionRibbon from './ConnectionRibbon.vue';
import MarketRibbon from './MarketRibbon.vue';
import MarketPromotionRibbon from './new-market-promotion/MarketPromotionRibbon.vue';
import useMarketPromotionRibbon from './new-market-promotion/usMarketPromotionRibbon';
import SecurityRibbon from './security/SecurityRibbon.vue';
import UpdateRibbon from './UpdateRibbon.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'RibbonSection',
  props: {
  delay: { type: Number, default: 10_000 },
  marketId: { type: String, required: false },
},
  setup(__props) {

const props = __props;

const route = useRoute();
const { isXS } = useMQBreakpoints();
const { showMarketPromotionRibbon, onCloseMarketPromotion } = useMarketPromotionRibbon();

const { securityShow } = accountService;
const { getMarket, getMarketName, getMarketState } = marketService;

let delayTimer;
const delayIsFinished = ref(false);
const showWsMaxConnectAttemptSpot = ref(false);
const showWsMaxConnectAttemptEotc = ref(false);

const market = computed(() => getMarket(props.marketId));
const marketState = computed(() => getMarketState(market.value));
const marketName = computed(() => getMarketName(market.value));

const showMarketRibbon = computed(() => marketState.value && [MarketState.SUSPENDED, MarketState.IN_MAINTENANCE].includes(marketState.value));

const { verification } = launchdarkly.flags;

const isSWUpdateAvailable = computed(() => SWAppUpdateChecker.serviceWorkerUpdated.value);
const isSpotPage = computed(() => route.name === 'spot');
const isEotcPage = computed(() => route.name === 'portfolio');

const showVerify = securityShow.verify;

const onCloseConnectionRibbon = (name: WebSocketManagerName) => {
  if (name === 'fastWebSocketManager') {
    showWsMaxConnectAttemptSpot.value = false;
  }

  if (name === 'eotcWebSocketManager') {
    showWsMaxConnectAttemptEotc.value = false;
  }
};
const showMaxConnectAttempt = (name: WebSocketManagerName) => {
  if (name === 'bunWebSocketManager') {
    return;
  }

  if (name === 'fastWebSocketManager') {
    showWsMaxConnectAttemptSpot.value = true;
  }

  if (name === 'eotcWebSocketManager') {
    showWsMaxConnectAttemptEotc.value = true;
  }
};
const hideMaxConnectAttempt = (name: WebSocketManagerName) => {
  if (name === 'bunWebSocketManager') {
    return;
  }

  if (name === 'fastWebSocketManager') {
    showWsMaxConnectAttemptSpot.value = false;
  }

  if (name === 'eotcWebSocketManager') {
    showWsMaxConnectAttemptEotc.value = false;
  }
};
const listenOnMaxConnectAttempt = onMaxConnectAttempt(showMaxConnectAttempt);
const listenOnConnected = onMaxConnectAttempt(hideMaxConnectAttempt);

wsEmitter.on('max-connect-attempt', listenOnMaxConnectAttempt);
wsEmitter.on('connected', listenOnConnected);

onMounted(() => {
  delayTimer = setTimeout(() => {
    delayIsFinished.value = true;
  }, props.delay);
});

onBeforeUnmount(() => {
  clearTimeout(delayTimer);
  wsEmitter.off('max-connect-attempt', listenOnMaxConnectAttempt);
  wsEmitter.off('connected', listenOnConnected);
});

return (_ctx: any,_cache: any) => {
  const _component_ot_transition_collapse = _resolveComponent("ot-transition-collapse")!

  return (_openBlock(), _createBlock(_component_ot_transition_collapse, null, {
    default: _withCtx(() => [
      (delayIsFinished.value)
        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
            (isSWUpdateAvailable.value)
              ? (_openBlock(), _createBlock(UpdateRibbon, {
                  key: "update-ribbon",
                  class: "ribbon-section"
                }))
              : (showWsMaxConnectAttemptSpot.value && isSpotPage.value)
                ? (_openBlock(), _createBlock(ConnectionRibbon, {
                    key: "connection-ribbon-exchange",
                    type: "exchange",
                    class: "ribbon-section",
                    onClose: _cache[0] || (_cache[0] = ($event: any) => (onCloseConnectionRibbon('fastWebSocketManager')))
                  }))
                : (showWsMaxConnectAttemptEotc.value && isEotcPage.value)
                  ? (_openBlock(), _createBlock(ConnectionRibbon, {
                      key: "connection-ribbon-eotc",
                      type: "eotc",
                      class: "ribbon-section",
                      onClose: _cache[1] || (_cache[1] = ($event: any) => (onCloseConnectionRibbon('eotcWebSocketManager')))
                    }))
                  : (_unref(showMarketPromotionRibbon))
                    ? (_openBlock(), _createBlock(MarketPromotionRibbon, {
                        key: "market-promotion-ribbon",
                        class: "ribbon-section",
                        onCloseMarketPromotion: _unref(onCloseMarketPromotion)
                      }, null, 8, ["onCloseMarketPromotion"]))
                    : (showMarketRibbon.value)
                      ? (_openBlock(), _createBlock(MarketRibbon, {
                          key: "market-ribbon",
                          class: "ribbon-section",
                          "market-name": marketName.value,
                          "market-state": marketState.value
                        }, null, 8, ["market-name", "market-state"]))
                      : (_unref(showVerify) && !_unref(isXS))
                        ? (_openBlock(), _createBlock(SecurityRibbon, {
                            key: "security-ribbon",
                            class: "ribbon-section",
                            verification: _unref(verification)
                          }, null, 8, ["verification"]))
                        : _createCommentVNode("", true)
          ], 64))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})